import { useState, useEffect, useRef, useCallback } from 'react'
import { useNavigate } from 'react-router'
import {
  Search, MapPin, Wrench, Building2, Loader2,
  Navigation, Clock, TrendingUp, ChevronRight, X,
} from 'lucide-react'

// ─── Types ───────────────────────────────────────────────────

interface CategoryResult {
  id: string
  name: string
  slug: string
  parentId: string | null
  icon?: string
  parentName?: string
}

interface CityResult {
  communeCode: string
  name: string
  postalCode: string
  departmentName: string
}

interface GeoPosition {
  lat: number
  lng: number
  city?: string
  postalCode?: string
}

type SearchSection = 'popular' | 'recent' | 'categories' | 'cities'

interface SearchItem {
  section: SearchSection
  type: 'category' | 'city' | 'geo' | 'action'
  label: string
  sublabel?: string
  slug?: string
  data?: CategoryResult | CityResult
}

// ─── Constants ───────────────────────────────────────────────

const POPULAR_SERVICES = [
  { name: 'Plomberie', slug: 'plomberie' },
  { name: 'Électricité', slug: 'electricite' },
  { name: 'Peinture', slug: 'peinture' },
  { name: 'Rénovation', slug: 'renovation' },
  { name: 'Serrurerie', slug: 'serrurerie' },
  { name: 'Climatisation', slug: 'climatisation' },
]

const RECENT_SEARCHES_KEY = 'afficheservices_recent_searches'
const MAX_RECENT = 5

// ─── Helpers ─────────────────────────────────────────────────

function getRecentSearches(): Array<{ query: string; location?: string; slug?: string }> {
  try {
    const raw = localStorage.getItem(RECENT_SEARCHES_KEY)
    return raw ? JSON.parse(raw) : []
  } catch {
    return []
  }
}

function saveRecentSearch(entry: { query: string; location?: string; slug?: string }) {
  try {
    const existing = getRecentSearches().filter(
      (s) => s.query !== entry.query || s.location !== entry.location,
    )
    const updated = [entry, ...existing].slice(0, MAX_RECENT)
    localStorage.setItem(RECENT_SEARCHES_KEY, JSON.stringify(updated))
  } catch {
    // localStorage unavailable
  }
}

// ─── Component ───────────────────────────────────────────────

interface SearchAutocompleteProps {
  variant?: 'hero' | 'compact'
  defaultQuery?: string
  defaultLocation?: string
}

export function SearchAutocomplete({
  variant = 'hero',
  defaultQuery = '',
  defaultLocation = '',
}: SearchAutocompleteProps) {
  const navigate = useNavigate()
  const containerRef = useRef<HTMLDivElement>(null)
  const inputRef = useRef<HTMLInputElement>(null)
  const locationInputRef = useRef<HTMLInputElement>(null)

  // ── State ──────────────────────────────────────────────────

  const [query, setQuery] = useState(defaultQuery)
  const [location, setLocation] = useState(defaultLocation)
  const [categories, setCategories] = useState<CategoryResult[]>([])
  const [cities, setCities] = useState<CityResult[]>([])
  const [loading, setLoading] = useState(false)
  const [locationLoading, setLocationLoading] = useState(false)
  const [selectedCategory, setSelectedCategory] = useState<CategoryResult | null>(null)
  const [selectedCity, setSelectedCity] = useState<CityResult | null>(null)

  // Dropdown state
  const [showDropdown, setShowDropdown] = useState(false)
  const [activeField, setActiveField] = useState<'service' | 'location' | null>(null)
  const [highlightIndex, setHighlightIndex] = useState(-1)

  // Geolocation
  const [geoPosition, setGeoPosition] = useState<GeoPosition | null>(null)
  const [geoLoading, setGeoLoading] = useState(false)
  const [geoError, setGeoError] = useState(false)

  // Recent searches
  const [recentSearches, setRecentSearches] = useState<
    Array<{ query: string; location?: string; slug?: string }>
  >([])

  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const locationDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  // ── Load recent searches on mount ──────────────────────────

  useEffect(() => {
    setRecentSearches(getRecentSearches())
  }, [])

  // ── Build dropdown items ───────────────────────────────────

  const buildDropdownItems = useCallback((): SearchItem[] => {
    const items: SearchItem[] = []

    if (activeField === 'service') {
      // If user is typing, show search results
      if (query.length >= 2 && categories.length > 0) {
        categories.forEach((cat) => {
          items.push({
            section: 'categories',
            type: 'category',
            label: cat.name,
            sublabel: cat.parentId ? 'sous-catégorie' : undefined,
            slug: cat.slug,
            data: cat,
          })
        })
      } else if (query.length < 2) {
        // Show recent searches first
        if (recentSearches.length > 0) {
          recentSearches.forEach((recent) => {
            items.push({
              section: 'recent',
              type: 'action',
              label: recent.query,
              sublabel: recent.location,
              slug: recent.slug,
            })
          })
        }

        // Show popular services
        POPULAR_SERVICES.forEach((svc) => {
          items.push({
            section: 'popular',
            type: 'category',
            label: svc.name,
            slug: svc.slug,
          })
        })
      }
    }

    if (activeField === 'location') {
      // Geolocation option always first
      if (!geoPosition && !geoError) {
        items.push({
          section: 'popular',
          type: 'geo',
          label: 'Utiliser ma position',
          sublabel: 'Détection automatique',
        })
      } else if (geoPosition?.city) {
        items.push({
          section: 'popular',
          type: 'geo',
          label: geoPosition.city,
          sublabel: `Position actuelle · ${geoPosition.postalCode ?? ''}`,
        })
      }

      // City search results
      if (location.length >= 2 && cities.length > 0) {
        cities.forEach((city) => {
          items.push({
            section: 'cities',
            type: 'city',
            label: city.name,
            sublabel: `${city.postalCode} · ${city.departmentName}`,
            data: city,
          })
        })
      }
    }

    return items
  }, [activeField, query, location, categories, cities, recentSearches, geoPosition, geoError])

  const dropdownItems = buildDropdownItems()

  // ── Debounced category search ──────────────────────────────

  useEffect(() => {
    if (query.length < 2) {
      setCategories([])
      return
    }
    if (debounceRef.current) clearTimeout(debounceRef.current)
    debounceRef.current = setTimeout(async () => {
      setLoading(true)
      try {
        const res = await fetch(`/api/search?q=${encodeURIComponent(query)}&type=category`)
        const data = await res.json()
        setCategories(data.categories ?? [])
      } catch {
        setCategories([])
      }
      setLoading(false)
    }, 150)
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current)
    }
  }, [query])

  // ── Debounced city search ──────────────────────────────────

  useEffect(() => {
    if (location.length < 2) {
      setCities([])
      return
    }
    if (locationDebounceRef.current) clearTimeout(locationDebounceRef.current)
    locationDebounceRef.current = setTimeout(async () => {
      setLocationLoading(true)
      try {
        const res = await fetch(`/api/search?q=${encodeURIComponent(location)}&type=city`)
        const data = await res.json()
        setCities(data.cities ?? [])
      } catch {
        setCities([])
      }
      setLocationLoading(false)
    }, 150)
    return () => {
      if (locationDebounceRef.current) clearTimeout(locationDebounceRef.current)
    }
  }, [location])

  // ── Close dropdown on outside click ────────────────────────

  useEffect(() => {
    function handleClick(e: MouseEvent) {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setShowDropdown(false)
        setActiveField(null)
      }
    }
    document.addEventListener('mousedown', handleClick)
    return () => document.removeEventListener('mousedown', handleClick)
  }, [])

  // ── Reset highlight when items change ──────────────────────

  useEffect(() => {
    setHighlightIndex(-1)
  }, [dropdownItems.length, activeField])

  // ── Geolocation ────────────────────────────────────────────

  const requestGeolocation = useCallback(() => {
    if (!navigator.geolocation) {
      setGeoError(true)
      return
    }
    setGeoLoading(true)
    navigator.geolocation.getCurrentPosition(
      async (pos) => {
        const { latitude, longitude } = pos.coords
        const geo: GeoPosition = { lat: latitude, lng: longitude }

        // Reverse geocode via API
        try {
          const res = await fetch(
            `https://api-adresse.data.gouv.fr/reverse/?lon=${longitude}&lat=${latitude}&limit=1`,
          )
          const data = await res.json()
          if (data.features?.length > 0) {
            const props = data.features[0].properties
            geo.city = props.city ?? props.label
            geo.postalCode = props.postcode
          }
        } catch {
          // Fallback — no city name
        }

        setGeoPosition(geo)
        setGeoLoading(false)

        if (geo.city) {
          setLocation(geo.postalCode ? `${geo.city} (${geo.postalCode})` : geo.city)
          setSelectedCity({
            communeCode: '',
            name: geo.city,
            postalCode: geo.postalCode ?? '',
            departmentName: '',
          })
        }
      },
      () => {
        setGeoError(true)
        setGeoLoading(false)
      },
      { enableHighAccuracy: false, timeout: 8000 },
    )
  }, [])

  // ── Item selection handler ─────────────────────────────────

  const handleSelectItem = useCallback(
    (item: SearchItem) => {
      if (item.type === 'geo') {
        requestGeolocation()
        return
      }

      if (item.type === 'category') {
        const cat = item.data as CategoryResult | undefined
        setSelectedCategory(cat ?? { id: '', name: item.label, slug: item.slug ?? '', parentId: null })
        setQuery(item.label)
        setShowDropdown(false)
        setActiveField(null)

        // Focus location if empty
        if (!location && !selectedCity) {
          setTimeout(() => locationInputRef.current?.focus(), 100)
        }
      }

      if (item.type === 'city') {
        const city = item.data as CityResult
        setSelectedCity(city)
        setLocation(`${city.name} (${city.postalCode})`)
        setShowDropdown(false)
        setActiveField(null)
      }

      if (item.type === 'action') {
        // Recent search replay
        if (item.slug) {
          navigate(`/services/${item.slug}`)
        } else {
          setQuery(item.label)
          if (item.sublabel) setLocation(item.sublabel)
          setShowDropdown(false)
          setActiveField(null)
        }
      }
    },
    [location, selectedCity, navigate, requestGeolocation],
  )

  // ── Keyboard navigation ────────────────────────────────────

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (!showDropdown || dropdownItems.length === 0) {
        if (e.key === 'Enter') {
          e.preventDefault()
          handleSubmit()
        }
        return
      }

      switch (e.key) {
        case 'ArrowDown':
          e.preventDefault()
          setHighlightIndex((prev) => (prev < dropdownItems.length - 1 ? prev + 1 : 0))
          break
        case 'ArrowUp':
          e.preventDefault()
          setHighlightIndex((prev) => (prev > 0 ? prev - 1 : dropdownItems.length - 1))
          break
        case 'Enter':
          e.preventDefault()
          if (highlightIndex >= 0 && highlightIndex < dropdownItems.length) {
            handleSelectItem(dropdownItems[highlightIndex])
          } else {
            handleSubmit()
          }
          break
        case 'Escape':
          setShowDropdown(false)
          setActiveField(null)
          break
        case 'Tab':
          if (activeField === 'service' && !e.shiftKey) {
            setActiveField('location')
            setShowDropdown(true)
          }
          break
      }
    },
    [showDropdown, dropdownItems, highlightIndex, handleSelectItem],
  )

  // ── Submit ─────────────────────────────────────────────────

  function handleSubmit(e?: React.FormEvent) {
    e?.preventDefault()
    setShowDropdown(false)
    setActiveField(null)

    const cityParam = selectedCity ? `?city=${encodeURIComponent(selectedCity.name)}` : ''
    const locationParam = !selectedCity && location.trim()
      ? `?city=${encodeURIComponent(location.trim())}`
      : cityParam

    if (selectedCategory) {
      saveRecentSearch({
        query: selectedCategory.name,
        location: selectedCity?.name ?? location,
        slug: selectedCategory.slug,
      })
      navigate(`/services/${selectedCategory.slug}${locationParam}`)
    } else if (query.trim()) {
      saveRecentSearch({ query: query.trim(), location: selectedCity?.name ?? location })
      navigate(`/recherche?q=${encodeURIComponent(query.trim())}${locationParam ? '&' + locationParam.slice(1) : ''}`)
    } else {
      navigate('/demande-devis/1')
    }
  }

  // ── Clear handlers ─────────────────────────────────────────

  function clearQuery() {
    setQuery('')
    setSelectedCategory(null)
    setCategories([])
    inputRef.current?.focus()
  }

  function clearLocation() {
    setLocation('')
    setSelectedCity(null)
    setGeoPosition(null)
    setCities([])
    locationInputRef.current?.focus()
  }

  // ── Section headers ────────────────────────────────────────

  function getSectionHeader(section: SearchSection): string | null {
    switch (section) {
      case 'recent': return 'Recherches récentes'
      case 'popular': return activeField === 'location' ? 'Localisation' : 'Populaires'
      case 'categories': return 'Services'
      case 'cities': return 'Villes'
      default: return null
    }
  }

  function getSectionIcon(item: SearchItem) {
    if (item.type === 'geo') return <Navigation className="h-4 w-4" />
    if (item.section === 'recent') return <Clock className="h-4 w-4" />
    if (item.section === 'popular' && item.type === 'category') return <TrendingUp className="h-4 w-4" />
    if (item.type === 'category') return <Wrench className="h-4 w-4" />
    if (item.type === 'city') return <Building2 className="h-4 w-4" />
    return <Search className="h-4 w-4" />
  }

  // ── Render dropdown ────────────────────────────────────────

  let lastSection: SearchSection | null = null

  const isCompact = variant === 'compact'

  return (
    <div ref={containerRef} className="relative" onKeyDown={handleKeyDown}>
      <form onSubmit={handleSubmit} className="relative">
        <div
          className={`flex flex-col gap-0 rounded-2xl border bg-white shadow-lg transition-all focus-within:shadow-xl ${
            showDropdown ? 'border-primary/30 ring-2 ring-primary/10' : 'border-border'
          } ${isCompact ? 'sm:flex-row sm:items-center sm:rounded-xl sm:p-1' : 'p-2 sm:flex-row sm:items-center'}`}
        >
          {/* ── Service input ─────────────────────────── */}
          <div className={`relative flex-1 ${isCompact ? '' : 'min-w-0'}`}>
            <Search className="pointer-events-none absolute left-3 top-1/2 h-[18px] w-[18px] -translate-y-1/2 text-slate-400" />
            <input
              ref={inputRef}
              type="text"
              value={selectedCategory ? selectedCategory.name : query}
              onChange={(e) => {
                setQuery(e.target.value)
                setSelectedCategory(null)
                setShowDropdown(true)
                setActiveField('service')
              }}
              onFocus={() => {
                setShowDropdown(true)
                setActiveField('service')
              }}
              placeholder="Quel service ?"
              className={`w-full border-0 bg-transparent pl-10 pr-8 text-sm text-slate-900 placeholder:text-slate-400 focus:outline-none ${
                isCompact ? 'py-2.5 rounded-lg' : 'py-3 rounded-xl'
              }`}
              autoComplete="off"
              role="combobox"
              aria-expanded={showDropdown && activeField === 'service'}
              aria-haspopup="listbox"
              aria-autocomplete="list"
            />
            {(query || selectedCategory) && (
              <button
                type="button"
                onClick={clearQuery}
                className="absolute right-2 top-1/2 -translate-y-1/2 rounded-full p-1 text-slate-300 hover:bg-slate-100 hover:text-slate-500"
                aria-label="Effacer la recherche"
              >
                <X className="h-3.5 w-3.5" />
              </button>
            )}
            {loading && (
              <Loader2 className="absolute right-8 top-1/2 h-4 w-4 -translate-y-1/2 animate-spin text-slate-300" />
            )}
          </div>

          <div className="mx-2 hidden h-8 w-px bg-border sm:block" />

          {/* ── Location input ────────────────────────── */}
          <div className={`relative ${isCompact ? 'sm:w-44' : 'sm:w-56'}`}>
            <MapPin className="pointer-events-none absolute left-3 top-1/2 h-[18px] w-[18px] -translate-y-1/2 text-slate-400" />
            <input
              ref={locationInputRef}
              type="text"
              value={selectedCity ? `${selectedCity.name}${selectedCity.postalCode ? ` (${selectedCity.postalCode})` : ''}` : location}
              onChange={(e) => {
                setLocation(e.target.value)
                setSelectedCity(null)
                setGeoPosition(null)
                setShowDropdown(true)
                setActiveField('location')
              }}
              onFocus={() => {
                setShowDropdown(true)
                setActiveField('location')
              }}
              placeholder="Ville ou code postal"
              className={`w-full border-0 bg-transparent pl-10 pr-8 text-sm text-slate-900 placeholder:text-slate-400 focus:outline-none ${
                isCompact ? 'py-2.5 rounded-lg' : 'py-3 rounded-xl'
              }`}
              autoComplete="off"
              role="combobox"
              aria-expanded={showDropdown && activeField === 'location'}
              aria-haspopup="listbox"
              aria-autocomplete="list"
            />
            {(location || selectedCity) && (
              <button
                type="button"
                onClick={clearLocation}
                className="absolute right-2 top-1/2 -translate-y-1/2 rounded-full p-1 text-slate-300 hover:bg-slate-100 hover:text-slate-500"
                aria-label="Effacer la localisation"
              >
                <X className="h-3.5 w-3.5" />
              </button>
            )}
            {(locationLoading || geoLoading) && (
              <Loader2 className="absolute right-8 top-1/2 h-4 w-4 -translate-y-1/2 animate-spin text-slate-300" />
            )}
          </div>

          {/* ── Submit button — always show label, otherwise it reads as a
                 redundant blank arrow next to the input's magnifier icon */}
          <button
            type="submit"
            aria-label="Trouver un artisan"
            className={`flex items-center justify-center gap-2 bg-primary font-semibold text-white shadow-sm transition-all hover:bg-primary-hover hover:shadow-md btn-press ${
              isCompact
                ? 'rounded-lg px-4 py-2.5 text-xs'
                : 'rounded-xl px-6 py-3 text-sm'
            }`}
          >
            <Search className={isCompact ? 'h-3.5 w-3.5' : 'h-4 w-4'} />
            <span>Trouver un artisan</span>
          </button>
        </div>
      </form>

      {/* ══════════════ Dropdown ══════════════ */}
      {showDropdown && dropdownItems.length > 0 && (
        <div
          className="absolute left-0 top-full z-50 mt-2 w-full overflow-hidden rounded-2xl border border-border bg-white py-1 shadow-xl animate-fade-in"
          role="listbox"
        >
          {dropdownItems.map((item, idx) => {
            // Section header
            const showHeader = item.section !== lastSection
            lastSection = item.section
            const header = showHeader ? getSectionHeader(item.section) : null

            return (
              <div key={`${item.section}-${item.label}-${idx}`}>
                {header && (
                  <div className="px-4 pb-1 pt-3 text-[11px] font-semibold uppercase tracking-wider text-slate-400">
                    {header}
                  </div>
                )}
                <button
                  type="button"
                  role="option"
                  aria-selected={highlightIndex === idx}
                  onClick={() => handleSelectItem(item)}
                  className={`flex w-full items-center gap-3 px-4 py-2.5 text-left text-sm transition-colors ${
                    highlightIndex === idx
                      ? 'bg-primary-50 text-primary'
                      : 'text-slate-700 hover:bg-slate-50'
                  }`}
                >
                  <span
                    className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-lg ${
                      item.type === 'geo'
                        ? 'bg-emerald-50 text-emerald-600'
                        : item.section === 'recent'
                          ? 'bg-slate-100 text-slate-400'
                          : item.section === 'popular'
                            ? 'bg-amber-50 text-amber-600'
                            : 'bg-primary-50 text-primary'
                    }`}
                  >
                    {getSectionIcon(item)}
                  </span>
                  <div className="min-w-0 flex-1">
                    <span className="font-medium text-slate-900">{item.label}</span>
                    {item.sublabel && (
                      <span className="ml-2 text-xs text-slate-400">{item.sublabel}</span>
                    )}
                  </div>
                  {(item.type === 'category' || item.type === 'action') && (
                    <ChevronRight className="h-3.5 w-3.5 shrink-0 text-slate-300" />
                  )}
                  {item.type === 'geo' && geoLoading && (
                    <Loader2 className="h-4 w-4 shrink-0 animate-spin text-emerald-500" />
                  )}
                </button>
              </div>
            )
          })}

          {/* Keyboard hint */}
          <div className="hidden items-center gap-3 border-t border-border px-4 py-2 sm:flex">
            <span className="text-[10px] text-slate-400">
              <kbd className="rounded border border-border bg-slate-50 px-1 py-0.5 font-mono text-[10px]">↑↓</kbd> naviguer
            </span>
            <span className="text-[10px] text-slate-400">
              <kbd className="rounded border border-border bg-slate-50 px-1 py-0.5 font-mono text-[10px]">↵</kbd> sélectionner
            </span>
            <span className="text-[10px] text-slate-400">
              <kbd className="rounded border border-border bg-slate-50 px-1 py-0.5 font-mono text-[10px]">esc</kbd> fermer
            </span>
          </div>
        </div>
      )}

      {/* No results state */}
      {showDropdown && activeField === 'service' && query.length >= 2 && !loading && categories.length === 0 && (
        <div className="absolute left-0 top-full z-50 mt-2 w-full rounded-2xl border border-border bg-white p-6 text-center shadow-xl animate-fade-in">
          <Search className="mx-auto h-8 w-8 text-slate-300" />
          <p className="mt-2 text-sm font-medium text-slate-600">
            Aucun service trouvé pour "{query}"
          </p>
          <p className="mt-1 text-xs text-slate-400">
            Essayez un autre terme ou{' '}
            <button
              type="button"
              onClick={() => {
                setShowDropdown(false)
                navigate('/demande-devis/1')
              }}
              className="text-primary hover:underline"
            >
              décrivez votre projet
            </button>
          </p>
        </div>
      )}
    </div>
  )
}
